import { supabase } from '../supabase/client';
import { isCompanyPremiumActive } from '../../utils/companyPremium';
import type { Company } from '../api/companyService';
import { NotificationService } from '../api/notificationService';

/**
 * 方案 A：過期當日即降級（is_premium=false、訂閱 status=expired）。
 * 保留 premium_until 供資料頁顯示歷史到期日；寬限僅意味可登入 profile/premium 續費，不延長權益。
 */
export async function syncCompanyPremiumExpiryStrict(
  companyId: string
): Promise<{ synced: boolean; company?: Pick<Company, 'isPremium' | 'premiumUntil'> }> {
  const now = new Date();
  const nowIso = now.toISOString();

  await supabase
    .from('premium_subscriptions')
    .update({ status: 'expired', updated_at: nowIso })
    .eq('company_id', companyId)
    .eq('status', 'active')
    .lt('expires_at', nowIso);

  const { data: companyRow, error } = await supabase
    .from('companies')
    .select('id, user_id, company_name, is_premium, premium_until')
    .eq('id', companyId)
    .maybeSingle();

  if (error || !companyRow) {
    return { synced: false };
  }

  const companySnapshot = {
    isPremium: Boolean(companyRow.is_premium),
    premiumUntil: companyRow.premium_until || undefined,
  };

  const stillActive = isCompanyPremiumActive(companySnapshot);
  let synced = false;

  if (companyRow.is_premium && !stillActive) {
    const { error: updateErr } = await supabase
      .from('companies')
      .update({
        is_premium: false,
        updated_at: nowIso,
      })
      .eq('id', companyId);

    if (!updateErr) {
      synced = true;
      companySnapshot.isPremium = false;

      if (companyRow.user_id) {
        const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
        const { data: existingExpiredNotice } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', companyRow.user_id)
          .eq('type', 'premium_expired')
          .eq('related_id', companyId)
          .gte('created_at', dayAgo)
          .maybeSingle();

        if (!existingExpiredNotice) {
          const expiryLabel = companyRow.premium_until
            ? new Date(companyRow.premium_until).toLocaleDateString('zh-TW')
            : '';
          try {
            await NotificationService.createNotification(
              companyRow.user_id,
              'premium_expired',
              '高級會員已到期',
              `您的高級會員已於 ${expiryLabel} 到期。請續費以恢復公司端功能與免佣金權益。`,
              companyId
            );
          } catch (e) {
            console.warn('premium_expired notification:', e);
          }
        }
      }
    }
  }

  return { synced, company: companySnapshot };
}

/** 批次同步（Edge Function 邏輯與前端共用概念） */
export async function listCompanyIdsWithStalePremium(): Promise<string[]> {
  const nowIso = new Date().toISOString();
  const { data } = await supabase
    .from('companies')
    .select('id')
    .eq('is_premium', true)
    .lt('premium_until', nowIso);

  return (data || []).map((r) => r.id as string);
}
